"use client";

import { useCart } from "@/contexts/cart-context";
import { CartStorage } from "@/libs/cart-storage";
import { LocalStorageUtils } from "@/libs/local-storage";
import { cn } from "@/libs/utils";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", name: "English", shortName: "EN" },
  { code: "hi", name: "Hindi", shortName: "HN" },
];

export const LanguageSelector = () => {
  const { i18n, t } = useTranslation();
  const { refreshCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  const handleLanguageChange = (languageCode: string) => {
    const currentLanguageCode = i18n.language;

    CartStorage.switchToLanguageCart(currentLanguageCode, languageCode);

    i18n.changeLanguage(languageCode);
    setIsOpen(false);

    // Store language preference in localStorage
    LocalStorageUtils.setItem("preferred-language", languageCode);

    refreshCart();
  };

  return (
    <div className="relative z-50">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 z-50 px-1 py-[2px] lg:py-1 lg:text-[16px] text-[9px] font-medium text-secondary hover:text-gray-900 transition-colors bg-white border-2 border-secondary rounded-full hover:border-gray-900 lg:w-auto w-auto"
        aria-label={t("common.language")}
        id="language"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="lg:h-6 lg:w-6 h-3 w-3"
        >
          <path
            d="M9.00001 18C7.76134 18 6.59467 17.7633 5.50001 17.29C4.40534 16.816 3.45201 16.1727 2.64001 15.36C1.82801 14.5473 1.18467 13.594 0.710006 12.5C0.235339 11.406 -0.00132773 10.2393 5.60224e-06 9C5.60224e-06 7.75733 0.236672 6.58967 0.710006 5.497C1.18401 4.40367 1.82734 3.45133 2.64001 2.64C3.45267 1.82867 4.40601 1.18533 5.50001 0.71C6.59467 0.236667 7.76134 0 9.00001 0C10.2427 0 11.4103 0.236667 12.503 0.71C13.5963 1.184 14.549 1.82733 15.361 2.64C16.173 3.45267 16.816 4.405 17.29 5.497C17.7633 6.59033 18 7.758 18 9C18 10.2387 17.7633 11.4053 17.29 12.5C16.816 13.5947 16.1727 14.548 15.36 15.36C14.5473 16.172 13.595 16.8153 12.503 17.29C11.411 17.7647 10.2433 18.0013 9.00001 18ZM9.00001 17.008C9.58667 16.254 10.0707 15.5137 10.452 14.787C10.8327 14.0603 11.1423 13.247 11.381 12.347H6.61901C6.88367 13.2977 7.19967 14.1363 7.56701 14.863C7.93501 15.5897 8.41267 16.3047 9.00001 17.008ZM7.72701 16.858C7.26034 16.308 6.83434 15.628 6.44901 14.818C6.06367 14.0087 5.77701 13.1847 5.58901 12.346H1.75401C2.32734 13.5893 3.13967 14.6093 4.19101 15.406C5.24301 16.202 6.42167 16.686 7.72701 16.858ZM10.273 16.858C11.5783 16.686 12.757 16.202 13.809 15.406C14.8603 14.6093 15.6727 13.5893 16.246 12.346H12.412C12.1587 13.1973 11.8397 14.0277 11.455 14.837C11.0697 15.647 10.6757 16.3213 10.273 16.858ZM1.34601 11.347H5.38101C5.30501 10.9363 5.25167 10.5363 5.22101 10.147C5.18901 9.75833 5.17301 9.376 5.17301 9C5.17301 8.624 5.18867 8.24167 5.22001 7.853C5.25134 7.46433 5.30467 7.06433 5.38001 6.653H1.34701C1.23834 6.99967 1.15334 7.37733 1.09201 7.786C1.03067 8.194 1.00001 8.59867 1.00001 9C1.00001 9.40133 1.03034 9.80633 1.09101 10.215C1.15167 10.6237 1.23667 11.0007 1.34601 11.346M6.38101 11.346H11.619C11.695 10.936 11.7483 10.5427 11.779 10.166C11.811 9.79 11.827 9.40133 11.827 9C11.827 8.59867 11.8113 8.21 11.78 7.834C11.7487 7.458 11.6953 7.06467 11.62 6.654H6.38001C6.30467 7.064 6.25134 7.45733 6.22001 7.834C6.18867 8.21 6.17301 8.59867 6.17301 9C6.17301 9.40133 6.18867 9.79 6.22001 10.166C6.25134 10.542 6.30567 10.9353 6.38101 11.346ZM12.62 11.346H16.654C16.7627 11 16.8477 10.623 16.909 10.215C16.9697 9.80633 17 9.40133 17 9C17 8.59867 16.9697 8.19367 16.909 7.785C16.8483 7.37633 16.7633 6.99933 16.654 6.654H12.619C12.695 7.064 12.7483 7.46367 12.779 7.853C12.811 8.24233 12.827 8.62467 12.827 9C12.827 9.37533 12.8113 9.75767 12.78 10.147C12.7487 10.5363 12.6953 10.9363 12.62 11.347M12.412 5.654H16.246C15.66 4.38467 14.8573 3.36467 13.838 2.594C12.8187 1.82333 11.6303 1.333 10.273 1.123C10.7397 1.737 11.1593 2.43933 11.532 3.23C11.9047 4.02 12.198 4.828 12.412 5.654ZM6.61901 5.654H11.381C11.117 4.71533 10.7913 3.86667 10.404 3.108C10.0167 2.34933 9.54867 1.644 9.00001 0.992C8.45134 1.64333 7.98334 2.34867 7.59601 3.108C7.20867 3.86733 6.88234 4.716 6.61901 5.654ZM1.75501 5.654H5.58901C5.80301 4.82867 6.09634 4.02067 6.46901 3.23C6.84167 2.43933 7.26134 1.737 7.72801 1.123C6.35867 1.33367 5.16734 1.827 4.15401 2.603C3.14067 3.38033 2.34067 4.397 1.75401 5.653"
            fill="black"
          />
        </svg>
        <span className="lg:block hidden">{currentLanguage.name}</span>
        <span className="lg:hidden block">{currentLanguage.shortName}</span>
        <ChevronDown
          className={cn(
            " w-2.5 md:w-3.5 h-3.5 text-black  transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className={cn("fixed inset-0 z-50", isOpen && "lg:mb-0 mb-20 ")}
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div
            className="absolute left-0 lg:top-full lg:mt-1 top-[calc(100%+1px)] bg-white border border-gray-200 rounded-md shadow-lg z-50"
            style={{ width: buttonWidth > 0 ? `${buttonWidth}px` : "auto" }}
          >
            <div className="py-1">
              {languages.map((language) => (
                <button
                  id="language-name"
                  aria-label="language name"
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={cn(
                    "w-full flex items-center px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                    i18n.language === language.code &&
                      "bg-gray-50 text-[#920000] font-medium",
                  )}
                >
                  <span className="lg:block hidden">{language.name}</span>
                  <span className="lg:hidden block">{language.shortName}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
